import { useEffect, useMemo, useRef, useState } from "react";
import "../../styles/website/Home.css";
import "../../styles/website/StoreBank.css";
import Layout from "../website/component/Layout";
import storebank from "../../assets/website/store-svgrepo.svg";
import storebankbackground from "../../assets/website/store-svgrepo-background.svg";
import { fetchFilterOptions, fetchPublicStores } from "../../api/shopsApi.js";

// آدرس پایه سایت — برای ساخت لینک کامل فروشگاه از store_url نسبی (مثل "/demo/")
const SITE_BASE_URL = "https://dokana.ir";

/* =========================================================
   ثابت‌ها
   ========================================================= */

const ALL_CATEGORY = "همه دسته‌ها";
const ALL_PROVINCE = "همه استان‌ها";
const PAGE_SIZE = 21; // تعداد کارت در هر صفحه (چون گرید ۳ ستونیه، مضرب ۳ گذاشتم)

// ---------------------------------------------------------
// گزینه‌های مرتب‌سازی — value همون چیزیه که به بک‌اند به عنوان
// پارامتر sort فرستاده می‌شه. برای اضافه کردن یک نوع مرتب‌سازی
// جدید، فقط یک آیتم به همین آرایه اضافه کن؛ دراپ‌داون، مقدار
// پیش‌فرض و درخواست به بک‌اند خودکار باهاش هماهنگ می‌شن.
// ---------------------------------------------------------
const SORT_OPTIONS = [
  { value: "newest", label: "جدیدترین" },
  { value: "oldest", label: "قدیمی‌ترین" },
  { value: "name_asc", label: "نام (الف تا ی)" },
  { value: "name_desc", label: "نام (ی تا الف)" },
  { value: "most_visited", label: "بیشترین بازدید" },
  { value: "least_visited", label: "کمترین بازدید" },
];
const DEFAULT_SORT = SORT_OPTIONS[0].value;

const CATEGORY_ICONS = {
  "پوشاک و مد": "👗",
  "لوازم آرایشی و بهداشتی": "💄",
  "کتاب و لوازم التحریر": "📚",
  "اسباب بازی و سرگرمی": "🧸",
  "محصولات غذایی و آشامیدنی": "🍫",
  "موبایل، تبلت و لوازم جانبی": "📱",
  "کامپیوتر و لوازم جانبی": "💻",
  "صوتی و تصویری": "🎧",
  "خودرو و لوازم جانبی": "🚗",
  "لوازم ورزشی و تناسب اندام": "🏋️",
  "مبلمان و دکوراسیون منزل": "🛋️",
  "لوازم دکوری و هنری": "🎨",
  "لوازم کودک و نوزاد": "🍼",
  "ابزارآلات و تجهیزات ساختمانی": "🛠️",
  "گل و گیاه و لوازم باغبانی": "🌿",
  "حیوانات خانگی و لوازم مربوطه": "🐾",
  "صنایع دستی و سوغاتی": "🎁",
  "لوازم سفر و کمپینگ": "🎒",
  "لوازم اداری و سازمانی": "🖇️",
  "لوازم خانگی": "🧺",
};

/* =========================================================
   کمکی‌ها
   ========================================================= */

// شماره صفحات با ... برای وقتی تعداد صفحات زیاده
function getPageNumbers(current, total) {
  const delta = 1;
  const range = [];
  const withDots = [];
  let last;

  for (let i = 1; i <= total; i++) {
    if (
      i === 1 ||
      i === total ||
      (i >= current - delta && i <= current + delta)
    ) {
      range.push(i);
    }
  }

  range.forEach((i) => {
    if (last) {
      if (i - last === 2) withDots.push(last + 1);
      else if (i - last !== 1) withDots.push("…");
    }
    withDots.push(i);
    last = i;
  });

  return withDots;
}

/* =========================================================
   ساب‌کامپوننت‌ها
   ========================================================= */

function StoreCard({ store, index }) {
  const href = store.url ? `${store.url}` : "#";

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="tile store-card"
      style={{ "--d": `${(index % 6) * 0.06}s` }}
    >
      <div className="store-card-media">
        {store.image ? (
          <img src={store.image} alt={store.name} loading="lazy" />
        ) : (
          <div className="store-card-media-fallback">
            <span>{CATEGORY_ICONS[store.category] || "🏪"}</span>
          </div>
        )}
        <span className="store-category store-category-floating">
          {CATEGORY_ICONS[store.category] || "🏪"} {store.category}
        </span>
      </div>

      <div className="store-card-body">
        <h3 className="store-name">{store.name}</h3>

        <div className="store-location">
          <span>📍</span>
          <span>
            {store.province} — {store.city}
          </span>
        </div>

        <span className="store-link">مشاهده فروشگاه ←</span>
      </div>
    </a>
  );
}

function StoreCardSkeleton({ index }) {
  return (
    <div
      className="tile store-card store-card-skeleton"
      style={{ "--d": `${(index % 6) * 0.06}s` }}
    />
  );
}

/* =========================================================
   کامپوننت اصلی
   ========================================================= */

export default function StoreBank() {
  // ---------- فیلترهای انتخابی کاربر ----------
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryId, setCategoryId] = useState(""); // "" یعنی همه دسته‌ها
  const [provinceId, setProvinceId] = useState(""); // "" یعنی همه استان‌ها
  const [cityId, setCityId] = useState("");
  const [sortOrder, setSortOrder] = useState(DEFAULT_SORT); // مقدار از SORT_OPTIONS

  // ---------- صفحه‌بندی (فرانت) ----------
  const [page, setPage] = useState(1);

  // ---------- گزینه‌های فیلتر (از بک‌اند) ----------
  const [filterOptions, setFilterOptions] = useState({
    provinces: [],
    cities: [],
    store_types: [],
  });
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [filtersError, setFiltersError] = useState(false);

  // ---------- نتیجه فروشگاه‌ها (از بک‌اند) ----------
  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(true);
  const [storesError, setStoresError] = useState(false);

  // ---------- ۱) اول گزینه‌های فیلتر رو می‌گیریم ----------
  useEffect(() => {
    let cancelled = false;

    async function loadFilters() {
      try {
        setFiltersLoading(true);
        const data = await fetchFilterOptions();
        if (cancelled) return;
        setFilterOptions({
          provinces: data.provinces || [],
          cities: data.cities || [],
          store_types: data.store_types || [],
        });
        setFiltersError(false);
      } catch (err) {
        if (!cancelled) setFiltersError(true);
      } finally {
        if (!cancelled) setFiltersLoading(false);
      }
    }

    loadFilters();
    return () => {
      cancelled = true;
    };
  }, []);

  // دیبانس جستجو — تا با هر حرف تایپ‌شده درخواست نره
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  // شهرهای مربوط به استان انتخابی
  const cities = useMemo(() => {
    if (!provinceId) return [];
    return filterOptions.cities.filter(
      (c) => String(c.province_id) === String(provinceId),
    );
  }, [provinceId, filterOptions.cities]);

  function handleProvinceChange(e) {
    setProvinceId(e.target.value);
    setCityId("");
  }

  function resetFilters() {
    setSearch("");
    setDebouncedSearch("");
    setCategoryId("");
    setProvinceId("");
    setCityId("");
    setSortOrder(DEFAULT_SORT);
  }

  const hasActiveFilters =
    debouncedSearch !== "" ||
    categoryId !== "" ||
    provinceId !== "" ||
    cityId !== "" ||
    sortOrder !== DEFAULT_SORT;

  // ---------- ۲) بعد از فیلتر، فروشگاه‌ها رو با همون فیلترها می‌گیریم ----------
  const requestIdRef = useRef(0);

  useEffect(() => {
    const currentRequestId = ++requestIdRef.current;

    async function loadStores() {
      try {
        setStoresLoading(true);
        setStoresError(false);

        const params = {};

        if (debouncedSearch) params.search = debouncedSearch;
        if (categoryId) params.store_type = categoryId;
        if (provinceId) params.province = provinceId;
        if (cityId) params.city = cityId;
        if (sortOrder) params.order = sortOrder; // newest | oldest — بک‌اند مرتب‌سازی می‌کنه

        const res = await fetchPublicStores(params);

        if (currentRequestId !== requestIdRef.current) return;

        const list = Array.isArray(res) ? res : res?.results || [];

        const mapped = list.map((s) => ({
          id: s.store_id,
          name: s.store_name,
          category: s.store_type_title,
          province: s.province_name,
          city: s.city_name,
          address: s.address,
          image: s.store_image,
          url: s.store_url,
          // اگه بک‌اند فیلد تاریخ ایجاد داره از همون استفاده می‌کنیم،
          // وگرنه fallback روی id (چون id افزایشیه و تقریب خوبی از قدیم/جدید بودنه)
          createdAt: s.created_at || s.store_created_at || s.created || null,
        }));

        setStores(mapped);
        setStoresError(false);
      } catch (err) {
        if (currentRequestId === requestIdRef.current) {
          setStoresError(true);
        }
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setStoresLoading(false);
        }
      }
    }

    loadStores();
  }, [debouncedSearch, categoryId, provinceId, cityId, sortOrder]);

  // ---------- ۳) مرتب‌سازی سمت بک‌اند انجام می‌شه؛ اینجا فقط همون خروجی رو استفاده می‌کنیم ----------
  const sortedStores = stores;

  // ---------- ۴) صفحه‌بندی سمت فرانت ----------
  const totalPages = Math.max(1, Math.ceil(sortedStores.length / PAGE_SIZE));

  useEffect(() => {
    // با تغییر فیلترها یا مرتب‌سازی، برگرد صفحه‌ی اول
    setPage(1);
  }, [debouncedSearch, categoryId, provinceId, cityId, sortOrder]);

  useEffect(() => {
    // اگه صفحه فعلی از تعداد کل صفحات بیشتر شد (مثلاً بعد فیلتر شدن نتایج کم شد)
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const paginatedStores = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sortedStores.slice(start, start + PAGE_SIZE);
  }, [sortedStores, page]);

  const pageNumbers = useMemo(
    () => getPageNumbers(page, totalPages),
    [page, totalPages],
  );

  function goToPage(p) {
    if (p < 1 || p > totalPages || p === page) return;
    setPage(p);
    // اسکرول نرم به بالای نتایج
    document
      .querySelector(".bank-results")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <Layout>
      {/* ---------- Hero ---------- */}
      <section className="bank-hero">
        <div className="wrap bank-hero-inner">
          <img className="image-store bank-hero-emoji" src={storebank} alt="" />
          <img
            className="image-store-background"
            src={storebankbackground}
            alt=""
          />

          <h1>بانک فروشگاه‌ها</h1>
          <p>فروشگاه‌های فعال سراسر ایران</p>
          <p className="bank-hero-sub">
            از میان هزاران فروشگاه فعال، فروشگاه مورد نظرت رو پیدا کن
          </p>
        </div>
      </section>

      {/* ---------- Search & filters ---------- */}
      <section className="bank-filters">
        <div className="wrap">
          <div className="section-head">
            <div>
              <h3> جستجو و فیلتر فروشگاه‌ها</h3>
            </div>
          </div>

          <div className="tile-search filters-panel">
            {filtersError && (
              <p className="shops-empty">
                خطا در دریافت فیلترها. لطفاً صفحه رو رفرش کن.
              </p>
            )}

            <div className="filters-grid">
              <div className="filter-field">
                <label htmlFor="store-search">نام فروشگاه</label>
                <input
                  id="store-search"
                  type="text"
                  placeholder="جستجوی نام فروشگاه..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="filter-field">
                <label htmlFor="store-category">دسته‌بندی</label>
                <select
                  id="store-category"
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  disabled={filtersLoading}
                >
                  <option value="">{ALL_CATEGORY}</option>
                  {filterOptions.store_types.map((t) => (
                    <option key={t.type_id} value={t.type_id}>
                      {t.title}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-field">
                <label htmlFor="store-province">استان</label>
                <select
                  id="store-province"
                  value={provinceId}
                  onChange={handleProvinceChange}
                  disabled={filtersLoading}
                >
                  <option value="">{ALL_PROVINCE}</option>
                  {filterOptions.provinces.map((p) => (
                    <option key={p.province_id} value={p.province_id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-field">
                <label htmlFor="store-city">شهر</label>
                <select
                  id="store-city"
                  value={cityId}
                  onChange={(e) => setCityId(e.target.value)}
                  disabled={
                    filtersLoading || !provinceId || cities.length === 0
                  }
                >
                  <option value="">
                    {!provinceId ? "ابتدا استان انتخاب کنید" : "همه شهرها"}
                  </option>
                  {cities.map((c) => (
                    <option key={c.city_id} value={c.city_id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-field">
                <label htmlFor="store-sort">مرتب‌سازی</label>
                <select
                  id="store-sort"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {hasActiveFilters && (
              <button
                type="button"
                className="btn btn-ghost filters-reset"
                onClick={resetFilters}
              >
                پاک کردن فیلترها ✕
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ---------- Results ---------- */}
      <section className="bank-results">
        <div className="wrap">
          {!storesLoading && !storesError && (
            <div className="results-count">
              <b>{sortedStores.length}</b> فروشگاه یافت شد
            </div>
          )}

          {storesLoading && (
            <div className="stores-grid">
              {Array.from({ length: 8 }).map((_, i) => (
                <StoreCardSkeleton key={i} index={i} />
              ))}
            </div>
          )}

          {!storesLoading && storesError && (
            <div className="no-results">
              <span>😕</span>
              <p>خطا در دریافت فروشگاه‌ها. لطفاً بعداً دوباره امتحان کن.</p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  resetFilters();
                }}
              >
                تلاش دوباره
              </button>
            </div>
          )}

          {!storesLoading && !storesError && sortedStores.length === 0 && (
            <div className="no-results">
              <span>😕</span>
              <p>هیچ فروشگاهی با این فیلترها پیدا نشد.</p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={resetFilters}
              >
                پاک کردن فیلترها
              </button>
            </div>
          )}

          {!storesLoading && !storesError && paginatedStores.length > 0 && (
            <>
              <div className="stores-grid">
                {paginatedStores.map((store, i) => (
                  <StoreCard key={store.id} store={store} index={i} />
                ))}
              </div>

              {totalPages > 1 && (
                <nav className="bank-pagination" aria-label="صفحه‌بندی نتایج">
                  <button
                    type="button"
                    className="pagination-btn"
                    onClick={() => goToPage(page - 1)}
                    disabled={page === 1}
                  >
                    → قبلی
                  </button>

                  <div className="pagination-numbers">
                    {pageNumbers.map((p, idx) =>
                      p === "…" ? (
                        <span key={`dots-${idx}`} className="pagination-dots">
                          …
                        </span>
                      ) : (
                        <button
                          key={p}
                          type="button"
                          className={
                            "pagination-page" +
                            (p === page ? " pagination-page-active" : "")
                          }
                          onClick={() => goToPage(p)}
                        >
                          {p}
                        </button>
                      ),
                    )}
                  </div>

                  <button
                    type="button"
                    className="pagination-btn"
                    onClick={() => goToPage(page + 1)}
                    disabled={page === totalPages}
                  >
                    بعدی ←
                  </button>
                </nav>
              )}
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}
